/*
  <perk-meter> — Progress-to-Perk meter (PDP)
  -------------------------------------------
  Fills a bar toward the store's perk minimums as the shopper changes quantity,
  answering "after I add this, do I qualify?". The two tiers test different
  prices (docs/DECEMBER_CROSS_SELL_BUILD.md, S2):

    tier 1 — the site-wide discount's minimum — on TAGGED prices:
        listed    = cart before discounts  +  picker qty × variant price
    tier 2 — the free Express rate — on the cart AFTER that discount, which
    Shopify takes off each unit, rounded down, once tier 1 is met (and then off
    the lines already in the cart too):
        projected = cart after discounts   +  picker qty × discounted price

  The page shows tagged prices, so tier 2's "Add $X more" is scaled back to
  them. The bar measures after-discount value throughout, below tier 1 too, so
  it never steps back when the discount starts.

  IN THE CART ALREADY (2026-10-15): once the buy box's variant is in the cart
  (data-variant-id, kept current on variant swaps), the meter stops projecting
  and reads the cart as the drawer's free-shipping bar does
  (snippets/cart-gap-closer.liquid): the cart after discounts against tier 2,
  the gap to the cent (".00" dropped), the fill floored, the picker ignored —
  so the page and the drawer never disagree after an add. Before this rule an
  add from the page left the meter projecting a SECOND unit: "unlocked 🎉" on
  a $35.20 cart while the drawer said "Add $14.80 more". Pages that carry an
  offer tier (data-a4-ids set) keep projecting after the add: one more tablet
  or shark IS the offer. The cart reading drops the tier-1 lead ("20% off
  unlocked.") — every cart line already prints the discount.

  Data attributes (all set server-side from the section schema):
    data-qty-input   — id of the quantity <input> to track
    data-unit-price  — current variant price, in cents
    data-variant-id  — the variant the buy box holds (the cart line's variant_id)
    data-tier1       — lower threshold (the site-wide discount's minimum), in cents; 0 = no tier 1
    data-tier2       — upper threshold (the free Express minimum), in cents
    data-tier1-label / data-tier2-label — perk names woven into the copy
    data-discount-pct — the site-wide discount's percent (Theme settings); 0 = none

  Offer tier — the ScribblePop party pack (A4, docs/SCRIBBLEPOP_A4_BUILD.md §4
  step 5) or the Baby Shark pair (docs/BABY_SHARK_UPSELL_BUILD.md §5), whichever
  the section's perk_offer names — set only when this product is in the offer's
  collection:
    data-a4-ids      — that collection's product ids, comma-separated
    data-a4-min      — the offer's minimum quantity
    data-a4-label    — the perk name that replaces tier 1's once
                       picker qty + those products already in the cart >= min
    data-a4-pct      — the offer's percent, which then prices the page item
                       in place of the site-wide one
    data-a4-variants — this product's variant ids. The picker counts toward the
                       minimum only while the buy box holds one of them: the
                       Pack Choice's bundle row (assets/pack-choice.js)
                       publishes the BUNDLE's variant, and a bundle is no offer
                       product, so the cart's own count decides then, and the
                       page item keeps the site-wide percent.
  The copy never states the percent: it lives in the label Liquid wrote, and
  no product handle reaches this file.

  The cart is re-read on every cartUpdate, from any source: an add from this
  page (the buy box, the bundle card, a card, the drawer's tiles) changes both
  the subtotal and the party-pack count the copy depends on.

  Enhancement only: the picker + Add to Cart work without this. If the cart
  fetch fails we fall back to a line-only projection (subtotal = 0).
*/
if (!customElements.get('perk-meter')) {
  class PerkMeter extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.pdp-perk-meter__track');
      this.copyEl = this.querySelector('.pdp-perk-meter__copy');
      if (!this.track || !this.copyEl) return;

      this.unitPrice = parseInt(this.dataset.unitPrice, 10) || 0;
      this.tier1 = parseInt(this.dataset.tier1, 10) || 0;
      this.tier2 = parseInt(this.dataset.tier2, 10) || 0;
      this.tier1Label = this.dataset.tier1Label || '';
      this.tier2Label = this.dataset.tier2Label || '';
      this.currency =
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      this.discountPct = this.percent(this.dataset.discountPct);
      this.variantId = this.dataset.variantId || '';
      this.cartSubtotal = 0;
      this.cartListed = 0;
      this.cartTotal = 0;
      this.cartLines = []; // { v: variant_id, q: quantity } — the in-cart count is read off these per render
      this.cartGain = 0;
      this.a4Ids = (this.dataset.a4Ids || '').split(',').filter(Boolean);
      this.a4Min = parseInt(this.dataset.a4Min, 10) || 0;
      this.a4Label = this.dataset.a4Label || '';
      this.a4Pct = this.percent(this.dataset.a4Pct);
      this.a4Variants = (this.dataset.a4Variants || '').split(',').filter(Boolean);
      this.a4Page = true; // the page loads holding its own product
      this.a4InCart = 0;

      if (this.tier2 <= 0) return; // nothing to fill toward

      const inputId = this.dataset.qtyInput;
      this.input = inputId ? document.getElementById(inputId) : null;

      // The picker dispatches a bubbling 'change' on the input for both typing
      // and the +/- buttons (see QuantityInput in global.js), so one listener covers both.
      if (this.input) {
        this._onQty = () => this.render();
        this.input.addEventListener('change', this._onQty);
      }

      // Variant swaps change the unit price — repaint when they do.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._unsub = subscribe(PUB_SUB_EVENTS.variantChange, (evt) => {
          const v = evt && evt.data && evt.data.variant;
          if (v && typeof v.price === 'number') {
            this.unitPrice = v.price;
            if (v.id != null) this.variantId = String(v.id);
            if (this.a4Variants.length && v.id != null) this.a4Page = this.a4Variants.includes(String(v.id));
            this.render();
          }
        });
        // Every source, 'cart-items' included: this reads /cart.js itself and
        // renders no cart region, so the render-suppression flag doesn't apply.
        this._unsubCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.fetchCart());
      }

      this.fetchCart();
    }

    disconnectedCallback() {
      if (this.input && this._onQty) this.input.removeEventListener('change', this._onQty);
      if (this._unsub) this._unsub();
      if (this._unsubCart) this._unsubCart();
    }

    fetchCart() {
      const base = (window.routes && window.routes.cart_url) || '/cart';
      fetch(`${base}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (cart) {
            this.cartSubtotal = cart.items_subtotal_price || 0;
            this.cartListed = cart.original_total_price || 0;
            this.cartTotal = cart.total_price || 0; // the drawer's bar reads this one
            this.cartLines = (cart.items || []).map((item) => ({ v: String(item.variant_id), q: item.quantity || 0 }));
            // What the site-wide discount would take off the lines that carry no
            // discount yet — used when this add is what lifts the cart over tier 1.
            this.cartGain = (cart.items || []).reduce(
              (gain, item) =>
                (item.line_level_discount_allocations || []).length > 0
                  ? gain
                  : gain + item.quantity * this.off(item.original_price || item.price || 0, this.discountPct),
              0
            );
            if (cart.currency) this.currency = cart.currency;
            this.a4InCart = (cart.items || []).reduce(
              (count, item) => (this.a4Ids.includes(String(item.product_id)) ? count + item.quantity : count),
              0
            );
          }
          this.render();
        })
        .catch(() => this.render());
    }

    qty() {
      const v = this.input ? parseInt(this.input.value, 10) : 1;
      return Number.isFinite(v) && v > 0 ? v : 1;
    }

    percent(raw) {
      const p = parseInt(raw, 10);
      return Number.isFinite(p) && p > 0 && p < 100 ? p : 0;
    }

    // Shopify's per-unit discount, rounded down: $19.99 at 20% → $3.99 off.
    off(cents, pct) {
      return Math.floor((cents * pct) / 100);
    }

    money(cents) {
      // Round UP to the next whole dollar, no decimals — a shopper who needs
      // $17.01 more must see "$18", never "$17" (which would fall short).
      const dollars = Math.ceil(cents / 100);
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
        }).format(dollars);
      } catch (e) {
        return '$' + dollars;
      }
    }

    // The drawer's print of the same gap (Liquid: money | remove: '.00'):
    // cents kept, a whole amount bare — "$14.80", "$6". Used by the cart reading only.
    moneyExact(cents) {
      let out;
      try {
        out = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (e) {
        out = '$' + (cents / 100).toFixed(2);
      }
      return out.replace(/\.00$/, '');
    }

    cap(s) {
      return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
    }

    inCartQty() {
      return this.cartLines.reduce((n, line) => (line.v === this.variantId ? n + line.q : n), 0);
    }

    // The cart reading (see the header): the drawer's bar, under the picker.
    renderCart() {
      const pct = Math.max(0, Math.min(100, Math.floor((this.cartTotal * 100) / this.tier2)));
      this.track.style.setProperty('--perk-pct', pct + '%');
      let copy;
      let state;
      if (this.tier1 > 0 && this.cartListed < this.tier1) {
        copy = `Add ${this.money(this.tier1 - this.cartListed)} to unlock ${this.tier1Label}`;
        state = 'tier0';
      } else if (this.cartTotal < this.tier2) {
        copy = `Add ${this.moneyExact(this.tier2 - this.cartTotal)} more for ${this.tier2Label}`;
        state = 'tier1';
      } else {
        copy = `${this.cap(this.tier2Label)} unlocked 🎉`;
        state = 'done';
      }
      this.copyEl.textContent = copy;
      this.dataset.state = state;
      this.toggleAttribute('data-a4-reached', false);
      this.toggleAttribute('data-in-cart', true);
    }

    render() {
      if (this.a4Ids.length === 0 && this.inCartQty() > 0) {
        this.renderCart();
        return;
      }
      this.toggleAttribute('data-in-cart', false);
      const qty = this.qty();
      // Offer reached: the tier-1 slot names that perk instead of the sitewide
      // one, and shows even where the section hides tier 1. The picker counts
      // only while it holds this product (see data-a4-variants).
      const pageQty = this.a4Page ? qty : 0;
      const a4 =
        this.a4Ids.length > 0 && this.a4Min > 0 && this.a4Label !== '' && pageQty + this.a4InCart >= this.a4Min;
      const tier1Label = a4 ? this.a4Label : this.tier1Label;
      const showTier1 = this.tier1 > 0 || a4;

      const listed = this.cartListed + this.unitPrice * qty;
      const rest = 100 - this.discountPct;
      let projected;
      let barValue;
      if (this.discountPct > 0 && listed >= this.tier1) {
        // The offer prices the page item in place of the site-wide discount —
        // when the page item is an offer product. Those already in the cart move
        // to it too, which is not modelled: at the live minimums (4 tablets,
        // about $104 tagged; 2 sharks, $93.98) every such cart is past tier 2
        // under either percent.
        const pagePct = a4 && this.a4Page && this.a4Pct > 0 ? this.a4Pct : this.discountPct;
        const cart = this.cartListed < this.tier1 ? this.cartSubtotal - this.cartGain : this.cartSubtotal;
        projected = cart + qty * (this.unitPrice - this.off(this.unitPrice, pagePct));
        barValue = projected;
      } else {
        projected = this.cartSubtotal + this.unitPrice * qty;
        barValue = Math.floor((listed * rest) / 100);
      }
      const pct = Math.max(0, Math.min(100, Math.round((barValue / this.tier2) * 100)));
      this.track.style.setProperty('--perk-pct', pct + '%');

      let copy;
      let state;
      if (this.tier1 > 0 && listed < this.tier1) {
        copy = `Add ${this.money(this.tier1 - listed)} to unlock ${tier1Label}`;
        state = 'tier0';
      } else if (projected < this.tier2) {
        const lead = showTier1 ? `${this.cap(tier1Label)} unlocked. ` : '';
        // In tagged prices: what to add at the prices the page shows.
        const toAdd = Math.ceil(((this.tier2 - projected) * 100) / rest);
        copy = `${lead}Add ${this.money(toAdd)} more for ${this.tier2Label}`;
        state = 'tier1';
      } else {
        copy = showTier1
          ? `${this.cap(tier1Label)} + ${this.tier2Label} unlocked 🎉`
          : `${this.cap(this.tier2Label)} unlocked 🎉`;
        state = 'done';
      }
      this.copyEl.textContent = copy;
      this.dataset.state = state;
      this.toggleAttribute('data-a4-reached', a4);
    }
  }

  customElements.define('perk-meter', PerkMeter);
}
